import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Pencil, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  agendaService,
  calendarCommunities,
  eventSchedule,
  weeklyEvent,
  type CalendarCommunity,
  type CalendarEvent,
  type CalendarInput,
  type CalendarSettings,
} from '@/services/agenda.service';
import { useMemberAreaProducts } from '@/hooks/useMemberAreas';
import { NativeSelect } from '@/components/ui/select';

const calendarSettingsKey = ['calendar', 'settings', 'products'] as const;

const emptyEvent = (): CalendarInput => ({
  title: '',
  description: '',
  community: 'GENERAL',
  eventType: 'LIVE',
  eventDate: '',
  startTime: '19:00',
  endTime: '',
  weekday: null,
  timezone: 'America/Sao_Paulo',
  meetingUrl: '',
  location: '',
  coverImage: '',
  isActive: true,
  isRecurring: false,
});

function EventEditor({
  event,
  onClose,
}: {
  event: CalendarEvent | null;
  onClose: () => void;
}) {
  const { id, ...initial } = event ?? { id: undefined, ...emptyEvent() };
  const [form, setForm] = useState<CalendarInput>({
    ...initial,
    isRecurring: event ? weeklyEvent(event) : false,
    eventDate: initial.eventDate?.slice(0, 10) ?? null,
  });
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      agendaService.saveEvent(
        {
          ...form,
          eventDate: form.isRecurring ? null : form.eventDate,
          weekday: form.isRecurring ? form.weekday : null,
          startTime: form.startTime || null,
          endTime: form.endTime || null,
        },
        id,
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['calendar'] });
      toast.success('Evento salvo na agenda compartilhada.');
      onClose();
    },
  });
  const change = <K extends keyof CalendarInput>(
    key: K,
    value: CalendarInput[K],
  ) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogTitle>{event ? 'Editar encontro' : 'Novo encontro'}</DialogTitle>
        <DialogDescription>
          As mudanças aparecem na home, nas aulas e na comunidade que tem
          acesso.
        </DialogDescription>
        <form
          className="space-y-4"
          onSubmit={(submit) => {
            submit.preventDefault();
            save.mutate();
          }}
        >
          <div>
            <Label htmlFor="event-title">Título</Label>
            <Input
              id="event-title"
              required
              maxLength={200}
              value={form.title}
              onChange={(e) => change('title', e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="event-community">Comunidade</Label>
              <NativeSelect
                id="event-community"
                value={form.community}
                onChange={(e) =>
                  change('community', e.target.value as CalendarCommunity)
                }
              >
                {Object.entries(calendarCommunities).map(([value, title]) => (
                  <option key={value} value={value}>
                    {title}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor="event-type">Tipo</Label>
              <NativeSelect
                id="event-type"
                value={form.eventType}
                onChange={(e) => change('eventType', e.target.value)}
              >
                {[
                  ['LIVE', 'Aula ao vivo'],
                  ['MEETING', 'Encontro'],
                  ['WORKSHOP', 'Workshop'],
                  ['QA', 'Perguntas e respostas'],
                  ['OTHER', 'Outro'],
                ].map(([value, title]) => (
                  <option key={value} value={value}>
                    {title}
                  </option>
                ))}
              </NativeSelect>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isRecurring}
              onChange={(e) =>
                setForm((current) => ({
                  ...current,
                  isRecurring: e.target.checked,
                  weekday: e.target.checked ? 1 : null,
                  eventDate: e.target.checked ? null : '',
                }))
              }
            />
            Repete toda semana
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            {form.isRecurring ? (
              <div>
                <Label htmlFor="event-weekday">Dia da semana</Label>
                <NativeSelect
                  id="event-weekday"
                  required
                  value={form.weekday ?? 1}
                  onChange={(e) => change('weekday', Number(e.target.value))}
                >
                  {[
                    'Segunda',
                    'Terça',
                    'Quarta',
                    'Quinta',
                    'Sexta',
                    'Sábado',
                    'Domingo',
                  ].map((day, index) => (
                    <option key={day} value={index + 1}>
                      {day}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            ) : (
              <div>
                <Label htmlFor="event-date">Data</Label>
                <div className="relative">
                  <Input
                    id="event-date"
                    type="date"
                    className="pr-10 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:top-1/2 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:-translate-y-1/2 [&::-webkit-calendar-picker-indicator]:opacity-0"
                    required
                    value={form.eventDate ?? ''}
                    onChange={(e) => change('eventDate', e.target.value)}
                  />
                  <CalendarDays aria-hidden="true" className="text-muted-foreground pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2" />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="event-timezone">Fuso horário</Label>
              <Input
                id="event-timezone"
                required
                value={form.timezone}
                onChange={(e) => change('timezone', e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="event-start">Início</Label>
              <Input
                id="event-start"
                type="time"
                required={!form.isRecurring}
                value={form.startTime ?? ''}
                onChange={(e) => change('startTime', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="event-end">Fim (opcional)</Label>
              <Input
                id="event-end"
                type="time"
                value={form.endTime ?? ''}
                onChange={(e) => change('endTime', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="event-description">Descrição</Label>
            <Textarea
              id="event-description"
              value={form.description ?? ''}
              onChange={(e) => change('description', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="event-url">Link do encontro</Label>
            <Input
              id="event-url"
              type="url"
              value={form.meetingUrl ?? ''}
              onChange={(e) => change('meetingUrl', e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="event-location">Local (opcional)</Label>
              <Input
                id="event-location"
                value={form.location ?? ''}
                onChange={(e) => change('location', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="event-image">Imagem de capa (URL)</Label>
              <Input
                id="event-image"
                type="url"
                value={form.coverImage ?? ''}
                onChange={(e) => change('coverImage', e.target.value)}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => change('isActive', e.target.checked)}
            />
            Visível para os alunos com acesso
          </label>
          {save.isError && (
            <p role="alert" className="text-destructive text-sm">
              Não foi possível salvar. Confira data, horário e conexão da
              agenda.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Salvando...' : 'Salvar encontro'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AccessSettings({ initial }: { initial: CalendarSettings }) {
  const client = useQueryClient();
  const [mapping, setMapping] = useState(initial.communityMemberAreaIds);
  const products = useMemberAreaProducts();
  const save = useMutation({
    mutationFn: () => agendaService.saveSettings(mapping),
    onSuccess: (settings) => {
      setMapping(settings.communityMemberAreaIds);
      client.setQueryData(calendarSettingsKey, settings);
      void client.invalidateQueries({ queryKey: ['calendar'] });
      toast.success('Acessos da agenda atualizados.');
    },
  });
  return (
    <details className="bg-card rounded-xl border p-5">
      <summary className="cursor-pointer text-base font-semibold">
        Quem acessa cada agenda
      </summary>
      <p className="text-muted-foreground my-4 max-w-prose text-sm">
        Selecione os cursos/produtos completos, como DevClub, IAClub, MBA e Seu
        Segundo Salário com IA. HTML, CSS e os demais módulos fazem parte desses
        produtos. O aluno vê a agenda quando tem acesso ativo a um produto
        selecionado. Eventos gerais ficam disponíveis para todos os alunos.
      </p>
      {initial.usesLegacyModuleAccess && (
        <div role="status" className="bg-muted/40 mb-4 rounded-lg border p-4 text-sm">
          <p>
            Os produtos abaixo são uma sugestão baseada na seleção anterior por
            módulos. Revise a seleção. Os acessos atuais serão mantidos até você salvar.
          </p>
          {initial.unmappedLegacyModulesCount > 0 && (
            <p className="text-muted-foreground mt-2">
              Há {initial.unmappedLegacyModulesCount} módulos sem produto associado
              na configuração anterior. Ao salvar, somente os produtos selecionados
              darão acesso às agendas.
            </p>
          )}
        </div>
      )}
      {products.isError && (
        <p role="alert" className="text-destructive text-sm">
          Não foi possível carregar os produtos.{' '}
          <Button variant="link" size="sm"
            type="button"
            onClick={() => void products.refetch()}
          >
            Tentar novamente
          </Button>
        </p>
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        {Object.entries(calendarCommunities)
          .filter(([key]) => key !== 'GENERAL')
          .map(([key, title]) => {
            const selected = mapping[key as CalendarCommunity] ?? [];
            return (
              <fieldset key={key} className="rounded-lg border p-4">
                <legend className="px-1 text-sm font-semibold">{title}</legend>
                <div className="max-h-44 space-y-2 overflow-y-auto">
                  {products.isLoading ? (
                    <p className="text-muted-foreground text-sm">
                      Carregando produtos...
                    </p>
                  ) : (
                    products.data?.map((product) => (
                      <label
                        key={product.id}
                        className="flex items-start gap-2 text-sm"
                      >
                        <input
                          className="mt-1"
                          type="checkbox"
                          disabled={save.isPending}
                          checked={selected.includes(product.id)}
                          onChange={(e) =>
                            setMapping((current) => ({
                              ...current,
                              [key]: e.target.checked
                                ? [...selected, product.id]
                                : selected.filter((id) => id !== product.id),
                            }))
                          }
                        />
                        <span>{product.name}{!product.isActive && <span className="text-muted-foreground"> · Inativo</span>}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-muted-foreground mt-3 text-xs">
                  {selected.length} produto(s) selecionado(s)
                </p>
              </fieldset>
            );
          })}
      </div>
      {save.isError && (
        <p role="alert" className="text-destructive mt-3 text-sm">
          Não foi possível salvar os acessos.
        </p>
      )}
      <Button
        className="mt-5"
        disabled={save.isPending || products.isError || products.isLoading}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Salvando...' : 'Salvar acessos'}
      </Button>
    </details>
  );
}

export function CalendarPage() {
  const [editing, setEditing] = useState<CalendarEvent | null | undefined>(
    undefined,
  );
  const settings = useQuery({
    queryKey: calendarSettingsKey,
    queryFn: agendaService.settings,
  });
  const events = useQuery({
    queryKey: ['calendar', 'admin'],
    queryFn: agendaService.adminEvents,
    enabled: settings.data?.configured === true,
  });
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Agenda unificada
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Gerencie os encontros da home, das aulas e do Works AI.
          </p>
        </div>
        <Button
          disabled={!settings.data?.configured}
          onClick={() => setEditing(null)}
        >
          <Plus className="h-4 w-4" />
          Novo encontro
        </Button>
      </header>
      {settings.isError && (
        <div role="alert" className="rounded-xl border p-5">
          Não foi possível carregar a configuração.{' '}
          <Button variant="outline" onClick={() => void settings.refetch()}>
            Tentar novamente
          </Button>
        </div>
      )}
      {settings.data && !settings.data.configured && (
        <p role="status" className="bg-card rounded-xl border p-5 text-sm">
          A agenda ainda não está conectada à home. Configure a integração no
          servidor para carregar e gerenciar os eventos.
        </p>
      )}
      {settings.data && <AccessSettings initial={settings.data} />}
      {events.isError ? (
        <div role="alert" className="bg-card rounded-xl border p-5">
          <p>A agenda da home está indisponível. Nenhum evento foi alterado.</p>
          <Button
            className="mt-3"
            variant="outline"
            onClick={() => void events.refetch()}
          >
            <RefreshCw className="h-4 w-4" />
            Tentar novamente
          </Button>
        </div>
      ) : events.isLoading ? (
        <p role="status">Carregando encontros...</p>
      ) : (
        <div className="bg-card divide-y overflow-hidden rounded-xl border">
          {events.data?.events.map((event) => (
            <article key={event.id} className="flex items-center gap-4 p-5">
              <CalendarDays className="text-primary hidden h-5 w-5 shrink-0 sm:block" />
              <div className="min-w-0 flex-1">
                <p className="text-muted-foreground text-xs">
                  {calendarCommunities[event.community]}
                  {!event.isActive ? ' · Oculto' : ''}
                </p>
                <h2 className="mt-1 font-semibold">{event.title}</h2>
                <p className="text-muted-foreground mt-1 text-sm">
                  {eventSchedule(event)}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(event)}
                aria-label={`Editar ${event.title}`}
              >
                <Pencil className="h-4 w-4" />
                Editar
              </Button>
            </article>
          ))}
          {events.data?.events.length === 0 && (
            <p className="text-muted-foreground p-6 text-sm">
              Crie o primeiro encontro da agenda compartilhada.
            </p>
          )}
        </div>
      )}
      {editing !== undefined && (
        <EventEditor event={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}
